import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { Lock, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';
import SEO from '@/components/common/SEO';

// ─── Design tokens ────────────────────────────────────────────────────────────
const UNI_MIDNIGHT = '#06060c';
const UNI_DARK = '#0a0a14';
const UNI_GOLD = '#d4a843';
const UNI_BORDER = 'rgba(255,255,255,0.07)';
const UNI_TEXT = 'rgba(255,255,255,0.88)';
const UNI_MUTED = 'rgba(255,255,255,0.45)';

export default function ResetPasswordPage() {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 820);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        if (password.length < 8) {
            setError('Password must be at least 8 characters long.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const { error } = await supabase.auth.updateUser({ password });
            if (error) throw error;
            setSuccess(true);
            setTimeout(() => navigate('/dashboard'), 3000);
        } catch (err: any) {
            console.error('Password update error:', err);
            setError(err.message || 'Failed to update password.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            background: UNI_MIDNIGHT,
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            fontFamily: "'Inter', sans-serif",
        }}>
            <SEO title="Reset Password" noindex />
            {/* Background Decor */}
            <div style={{
                position: 'fixed', top: '10%', left: '50%', transform: 'translateX(-50%)',
                width: 600, height: 600, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(212,168,67,0.05) 0%, transparent 70%)',
                pointerEvents: 'none',
            }} />

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                style={{
                    maxWidth: 420, width: '100%', position: 'relative', zIndex: 1,
                    background: UNI_DARK, border: `1px solid ${UNI_BORDER}`, borderRadius: 24,
                    padding: isMobile ? '32px 24px' : '48px 40px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                }}
            >
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <div style={{
                        width: 56, height: 56, borderRadius: 16, background: 'rgba(212,168,67,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: UNI_GOLD,
                        margin: '0 auto 20px', border: '1px solid rgba(212,168,67,0.2)'
                    }}>
                        <Lock size={28} />
                    </div>
                    <h1 style={{
                        fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 800,
                        margin: '0 0 8px', color: '#fff'
                    }}>
                        Set New Password
                    </h1>
                    <p style={{ color: UNI_MUTED, fontSize: 14 }}>
                        Enter a secure password for your Still Waters University account.
                    </p>
                </div>

                {success ? (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{
                            background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.2)',
                            borderRadius: 16, padding: '24px', textAlign: 'center',
                        }}
                    >
                        <CheckCircle2 size={40} color="#4ade80" style={{ margin: '0 auto 16px' }} />
                        <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Password Updated</div>
                        <p style={{ color: UNI_MUTED, fontSize: 14, margin: 0 }}>
                            Your password has been changed successfully. Redirecting to dashboard…
                        </p>
                    </motion.div>
                ) : (
                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        {error && (
                            <div style={{
                                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
                                borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10,
                                color: '#ef4444', fontSize: 13, fontWeight: 600,
                            }}>
                                <AlertCircle size={16} />
                                {error}
                            </div>
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <label style={{ fontSize: 12, fontWeight: 700, color: UNI_GOLD, letterSpacing: '1px' }}>NEW PASSWORD</label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="At least 8 characters"
                                    required
                                    style={{
                                        width: '100%', background: 'rgba(255,255,255,0.03)', border: `1px solid ${UNI_BORDER}`,
                                        borderRadius: 12, padding: '14px 16px', color: '#fff', fontSize: 15,
                                        outline: 'none', appearance: 'none', boxSizing: 'border-box',
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{
                                        position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                                        background: 'none', border: 'none', color: UNI_MUTED, cursor: 'pointer', padding: 4
                                    }}
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <label style={{ fontSize: 12, fontWeight: 700, color: UNI_GOLD, letterSpacing: '1px' }}>CONFIRM PASSWORD</label>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Repeat your password"
                                required
                                style={{
                                    width: '100%', background: 'rgba(255,255,255,0.03)', border: `1px solid ${UNI_BORDER}`,
                                    borderRadius: 12, padding: '14px 16px', color: '#fff', fontSize: 15,
                                    outline: 'none', appearance: 'none', boxSizing: 'border-box',
                                }}
                            />
                        </div>

                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            type="submit"
                            disabled={loading}
                            style={{
                                width: '100%', background: `linear-gradient(135deg, ${UNI_GOLD}, #b8912e)`,
                                color: '#06060c', border: 'none', borderRadius: 12, padding: '16px',
                                fontSize: 16, fontWeight: 800, cursor: 'pointer', marginTop: 12,
                                boxShadow: '0 8px 20px rgba(212,168,67,0.25)',
                            }}
                        >
                            {loading ? 'Updating…' : 'Reset Password →'}
                        </motion.button>
                    </form>
                )}
            </motion.div>
        </div>
    );
}
